import '../../design-system/control-consistency.css';
import '../../design-system/dashboard-polish.css';
import React, { useEffect, useId, useRef, useState } from 'react';
import '../../assets/css/tenant-dashboard-layout.css';
import '../../assets/css/tenant-dashboard-tokens.css';
import '../../design-system/refined-violet.css';

interface ExternalDashboardFrameProps {
  navigationLabel: string;
  routeKey: string;
  brand: React.ReactElement;
  search?: React.ReactElement;
  account: React.ReactElement;
  sidebar: React.ReactElement;
  children: React.ReactElement;
  menuIconClass: string;
  className?: string;
  websiteHref?: string;
}

/** Shared presentation boundary. Authentication, routing and role data stay in each app. */
export default function ExternalDashboardFrame({ navigationLabel, routeKey, brand, search, account, sidebar, children, menuIconClass, className = '', websiteHref }: ExternalDashboardFrameProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const sidebarId = `external-sidebar-${id}`;
  const mainId = `external-main-${id}`;
  const sidebarRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setOpen(false); mainRef.current?.scrollTo?.({ top: 0 }); }, [routeKey]);
  useEffect(() => {
    if (!open) return;
    const button = menuRef.current;
    const main = mainRef.current;
    if (main) main.inert = true;
    const links = sidebarRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    const first = links?.[0]; const last = links?.[links.length - 1];
    first?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setOpen(false); }
      if (event.key === 'Tab' && links?.length) {
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    const resize = () => { if (window.innerWidth > 991) setOpen(false); };
    document.addEventListener('keydown', keydown);
    window.addEventListener('resize', resize);
    return () => { document.removeEventListener('keydown', keydown); window.removeEventListener('resize', resize); if (main) main.inert = false; button?.focus(); };
  }, [open]);
  return <div className={`srs-tenant-app srs-layout-dashboard srs-dashboard-polish ${className}`} data-shell-id={id}>
    <a className="srs-external-skip" href={`#${mainId}`}>Skip to content</a>
    <header className="srs-tenant-topbar">
      <div className="srs-tenant-topbar-start">
        <button type="button" className="srs-tenant-menu-btn" ref={menuRef} aria-controls={sidebarId} aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} onClick={() => setOpen(value => !value)}><i className={menuIconClass} aria-hidden="true" /></button>
        {brand}
      </div>
      {search}
      <div className="srs-tenant-topbar-end">{websiteHref && <a className="srs-back-to-website" href={websiteHref} aria-label="Back to website"><span aria-hidden="true">↗</span><span className="srs-back-long">Back to website</span><span className="srs-back-short" aria-hidden="true">Website</span></a>}{account}</div>
    </header>
    {open && <button type="button" className="srs-tenant-sidebar-backdrop" aria-label="Close menu" onClick={() => setOpen(false)} />}
    <div className="srs-tenant-body">
      <aside className={`srs-tenant-sidebar-wrap${open ? ' is-open' : ''}`} id={sidebarId} ref={sidebarRef} role={open ? 'dialog' : undefined} aria-modal={open || undefined} aria-label={navigationLabel}>
        {open && <button type="button" className="srs-external-close" onClick={() => setOpen(false)}>Close menu</button>}
        {sidebar}
      </aside>
      <main className="srs-tenant-main" id={mainId} ref={mainRef} tabIndex={-1}>
        <div className="srs-tenant-module">{children}</div>
      </main>
    </div>
  </div>;
}
